'use client';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { AUDIO_LS_KEY, AUDIO_STARTED_EVENT } from './audio-constants';

function markAudioGenerating(slug: string) {
  if (typeof window === 'undefined') return;
  try {
    const raw = window.localStorage.getItem(AUDIO_LS_KEY);
    const map = raw ? JSON.parse(raw) : {};
    map[slug] = { startedAt: Date.now() };
    window.localStorage.setItem(AUDIO_LS_KEY, JSON.stringify(map));
  } catch { /* quota — non-fatal */ }
}

function clearAudioGenerating(slug: string) {
  if (typeof window === 'undefined') return;
  try {
    const raw = window.localStorage.getItem(AUDIO_LS_KEY);
    if (!raw) return;
    const map = JSON.parse(raw);
    delete map[slug];
    window.localStorage.setItem(AUDIO_LS_KEY, JSON.stringify(map));
  } catch { /* non-fatal */ }
}

// Categories that don't need audio (PDFs / non-story content, or webinars —
// which have no Listen pill / audio feature at all).
const RESOURCE_CATEGORIES = new Set([
  'Case Study',
  'White Paper',
  'Whitepaper',
  'Research Paper',
  'Industry Report',
  'Brochure',
  'Webinar',
]);

interface Props {
  slug: string;
  status: 'draft' | 'published';
  /** Present means audio already exists — skip generation step. */
  audioUrl?: string;
  /** Category from post frontmatter — used to decide if audio is needed. */
  category?: string;
}

interface ToastState {
  text: string;
  kind: 'info' | 'success' | 'warning' | 'error';
}

export function PublishToggle({ slug, status: initialStatus, audioUrl, category }: Props) {
  const router = useRouter();
  const [status, setStatus] = useState<'draft' | 'published'>(initialStatus);
  // Separate loading states: one for the publish API call, one for background audio.
  const [publishing, setPublishing] = useState(false);
  const [audioGenerating, setAudioGenerating] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);

  useEffect(() => { setStatus(initialStatus); }, [initialStatus]);

  const isStory = !category || !RESOURCE_CATEGORIES.has(category);
  const needsAudioGeneration = isStory && !audioUrl;

  // Fire audio generation in the background — does NOT block publish.
  const startAudioInBackground = (slug: string) => {
    setAudioGenerating(true);
    markAudioGenerating(slug);
    window.dispatchEvent(new CustomEvent(AUDIO_STARTED_EVENT, { detail: { slug } }));

    fetch('/api/generate-audio', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slug }),
    })
      .then(async res => {
        clearAudioGenerating(slug);
        setAudioGenerating(false);
        if (res.ok) {
          setToast({ text: 'Audio is ready — listeners can now play this post.', kind: 'success' });
          router.refresh();
        } else {
          let serverMsg = '';
          try { serverMsg = (await res.json())?.error ?? ''; } catch { /* ignore */ }
          setToast({
            text: `Audio generation failed: ${serverMsg || `HTTP ${res.status}`}. Use the "Generate audio" button on the post card to retry.`,
            kind: 'warning',
          });
        }
      })
      .catch(err => {
        clearAudioGenerating(slug);
        setAudioGenerating(false);
        setToast({
          text: `Audio generation failed (${(err as any)?.message ?? 'network error'}). Use the "Generate audio" button on the post card to retry.`,
          kind: 'warning',
        });
      });
  };

  const toggle = async () => {
    if (publishing) return;
    setPublishing(true);
    setToast(null);

    const action = status === 'published' ? 'unpublish' : 'publish';

    try {
      let res: Response;
      try {
        res = await fetch('/api/post/publish', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ slug, action }),
        });
      } catch (err) {
        setToast({
          text: `Network error while ${action === 'publish' ? 'publishing' : 'unpublishing'}: ${(err as any)?.message ?? 'unknown'}. Refresh the page to see the latest state.`,
          kind: 'error',
        });
        return;
      }

      if (!res.ok) {
        let serverMsg = '';
        try { serverMsg = (await res.json())?.error ?? ''; } catch { /* ignore */ }
        const isProxyTimeout = res.status === 502 || res.status === 504;
        setToast({
          text: isProxyTimeout
            ? `${action === 'publish' ? 'Publish' : 'Unpublish'} is still processing on the server (proxy returned ${res.status}). Refresh the admin page in a moment to see the result.`
            : `${action === 'publish' ? 'Publish' : 'Unpublish'} failed: ${serverMsg || `HTTP ${res.status}`}`,
          kind: isProxyTimeout ? 'warning' : 'error',
        });
        return;
      }

      const data = await res.json();
      const next = data?.status === 'published' || data?.status === 'draft'
        ? data.status
        : (status === 'published' ? 'draft' : 'published');
      setStatus(next);
      router.refresh();

      // Blog is live — now kick off audio in the background (stories only).
      if (action === 'publish' && needsAudioGeneration) {
        setToast({ text: 'Published! Audio is generating in the background…', kind: 'info' });
        startAudioInBackground(slug);
      }
    } finally {
      setPublishing(false);
    }
  };

  // Auto-dismiss toast after 15s (extended to 30s for audio-generating info toast).
  useEffect(() => {
    if (!toast) return;
    const delay = toast.kind === 'info' ? 30_000 : 15_000;
    const id = setTimeout(() => setToast(null), delay);
    return () => clearTimeout(id);
  }, [toast]);

  const toastColors: Record<ToastState['kind'], { bg: string; border: string; text: string }> = {
    info:    { bg: 'color-mix(in srgb, var(--seasons-color-cyber-blue, #053bee) 8%, transparent)', border: 'color-mix(in srgb, var(--seasons-color-cyber-blue, #053bee) 30%, transparent)', text: '#1e40af' },
    success: { bg: '#dcfce7', border: '#86efac', text: '#166534' },
    warning: { bg: '#fef3c7', border: '#fbbf24', text: '#92400e' },
    error:   { bg: '#fee2e2', border: '#f87171', text: '#991b1b' },
  };
  const c = toast ? toastColors[toast.kind] : null;

  // Button label
  let label: string;
  if (publishing) {
    label = status === 'published' ? 'Unpublishing…' : 'Publishing…';
  } else if (audioGenerating) {
    label = 'Audio generating';
  } else {
    label = status === 'published' ? 'Unpublish' : 'Publish';
  }

  const isLoading = publishing || audioGenerating;

  return (
    <div style={{ position: 'relative', display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' }}>
      <button
        className="block-button-link"
        onClick={toggle}
        disabled={publishing} // allow clicking away while audio generates; disable only during publish call
        style={{
          opacity: isLoading ? 0.8 : 1,
          border: 0,
          background: 'none',
          padding: 0,
          cursor: publishing ? 'default' : 'pointer',
        }}
        title={
          status === 'published'
            ? 'Unpublish this post'
            : isStory && !audioUrl
              ? 'Publish post — audio will generate in the background after publishing'
              : 'Publish this post'
        }
      >
        <div
          className={`block-button block-button--sm ${status === 'published' ? 'block-button--outline' : 'block-button--midnight'}`}
          style={{ whiteSpace: 'nowrap', gap: isLoading ? 6 : undefined }}
        >
          {isLoading && (
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              style={{ flexShrink: 0, animation: 'be-spin 0.9s linear infinite' }}
              aria-hidden="true"
            >
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
            </svg>
          )}
          <div className="block-button__text">
            <span className="btn-primary-text">{label}</span>
            <span className="btn-hover-text">{label}</span>
          </div>
        </div>
      </button>

      <style>{`@keyframes be-spin { to { transform: rotate(360deg); } }`}</style>

      {toast && c && (
        <div
          role="alert"
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 6px)',
            right: 0,
            zIndex: 30,
            padding: '8px 10px',
            background: c.bg,
            border: `1px solid ${c.border}`,
            borderRadius: 6,
            fontSize: 11,
            color: c.text,
            maxWidth: 340,
            lineHeight: 1.4,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            whiteSpace: 'normal',
          }}
        >
          {toast.text}
        </div>
      )}
    </div>
  );
}
